<template>
  <v-navigation-drawer
    class="sidebar"
    floating
    width="290"
    clipped
    :value="value"
    app
    :permanent="$size == 'xl' || $size == 'lg'"
    @input="$emit('input', $event)"
  >
    <div class="logo">
      <img src="@/assets/logo-red.svg" alt="Logo" />
    </div>
    <v-list dense shaped>
      <ProjectSelector />

      <div v-for="(item, idx) in items" :key="idx">
        <v-list-group
          v-if="item.children"
          :group="item.to"
          class="sidebar-group"
        >
          <template v-slot:appendIcon>
            <v-icon class="sidebar-group__toggle-icon"> $chevron_down </v-icon>
          </template>
          <template v-slot:activator>
            <v-tooltip
              open-delay="500"
              :disabled="!item.tooltip"
              max-width="300"
              right
            >
              <template v-slot:activator="{ on, attrs }">
                <v-list-item v-bind="attrs" class="sidebar-item" v-on="on">
                  <v-list-item-icon>
                    <v-icon>${{ item.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title> {{ item.text }} </v-list-item-title>
                </v-list-item>
              </template>
              <span>{{ item.tooltip }}</span>
            </v-tooltip>
          </template>

          <div>
            <v-tooltip
              v-for="(subItem, sidx) in item.children"
              :key="sidx"
              open-delay="500"
              :disabled="!subItem.tooltip"
              max-width="300"
              right
            >
              <template v-slot:activator="{ on, attrs }">
                <v-list-item
                  link
                  :to="subItem.to"
                  class="sidebar-subitem"
                  active-class="active"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-list-item-title>{{ subItem.text }}</v-list-item-title>
                </v-list-item>
              </template>
              <span>{{ subItem.tooltip }}</span>
            </v-tooltip>
          </div>
        </v-list-group>
        <v-tooltip
          v-else
          open-delay="500"
          :disabled="!item.tooltip"
          max-width="300"
          right
        >
          <template v-slot:activator="{ on, attrs }">
            <v-list-item
              :to="item.to"
              class="sidebar-item"
              active-class="active"
              v-bind="attrs"
              v-on="on"
            >
              <v-list-item-icon>
                <v-icon>${{ item.icon }}</v-icon>
              </v-list-item-icon>

              <v-list-item-title>{{ item.text }}</v-list-item-title>
            </v-list-item>
          </template>
          <span>{{ item.tooltip }}</span>
        </v-tooltip>
      </div>
    </v-list>
    <EnvSelector />
  </v-navigation-drawer>
</template>

<script>
export default {
  name: "SideBar",
  props: {
    value: Boolean,
  },
  data() {
    return {
      activeGroup: 0,
    };
  },
  computed: {
    items() {
      const hiddenIds =
        this.currentUser.active_project.hidden_sidebar_items || [];

      const items = [
        ...[
          {
            id: "dashboard",
            icon: "eye",
            text: "Dashboard",
            to: "/",
            tooltip: `This is your home base. It displays an overview of events, UGC submissions, site views, and more. It also shows a preview of your main “timeline”`,
          },
          {
            id: "theme",
            icon: "cog",
            text: "Theme",
            to: "/themes",
            tooltip: `Themes allow you to change the appearance of your Commons Page(s). Colors, fonts, default images, and more can be customized here.`,
          },
          {
            id: "pages",
            icon: "file",
            text: "Pages",
            to: "/pages",
            tooltip: `Pages are the hubs for any type of content on Commons. It’s where you should start when building your site. Pages can house as much or as little as you’d like from a series of live events to a simple submission form.`,
          },
          {
            id: "redirects",
            icon: "copy_link",
            text: "Redirects",
            to: "/redirects",
            tooltip: `Redirects send visitors from an old or shortened URL on your Commons domain to another URL. Use these to keep old links working or point traffic to a page outside Commons.`,
          },
          {
            id: "videos",
            icon: "vimeo",
            text: "Videos",
            to: "/videos",
            tooltip: `Videos let you reference a Vimeo video or upload an MP4 directly, complete with metadata pulled from Vimeo, an auto-generated preview clip, and call-to-action buttons.`,
          },
          ...(!this.currentUser.active_project.disable_online_events
            ? [
                {
                  id: "events",
                  icon: "calendar",
                  text: "Events",
                  to: "/events",
                  tooltip: `Events are the foundation of the guest experience. They can be live broadcasts, pre-recorded video, Zoom meetings, or static images. Every event has a “start time” and play on the “stage” so you can build out the way your attendees experience your content.`,
                },
              ]
            : []),
          ...(this.currentUser.active_project.available_addon_names.includes(
            "Addon::Ticketmaster"
          )
            ? [
                {
                  id: "ticketmaster__venue_events",
                  icon: "calendar",
                  text: "Venue Events",
                  to: "/venue-events",
                  tooltip: `Venue Events are live, in-person concerts, performances, musicals, etc.`,
                },
                {
                  id: "ticketmaster__venues",
                  icon: "map_marker",
                  text: "Venues",
                  to: "/ticketmaster-venues",
                  tooltip: `Venues are where Venue Events take place.`,
                },
              ]
            : []),
          ...(this.currentUser.active_project.available_addon_names.includes(
            "Addon::Greenhouse"
          )
            ? [
                {
                  id: "greenhouse",
                  icon: "form",
                  text: "Greenhouse",
                  tooltip: `Venue Events are live, in-person concerts, performances, musicals, etc.`,
                  children: [
                    {
                      id: "greenhouse__jobs",
                      text: "Jobs",
                      to: "/greenhouse/jobs",
                    },
                    {
                      id: "greenhouse__applications",
                      text: "Applications",
                      to: "/greenhouse/applications",
                    },
                  ],
                },
              ]
            : []),
          {
            id: "submission_forms",
            text: "Submission Forms",
            icon: "format_list_bulleted",
            to: "/widgets/submission-forms",
            tooltip:
              "Submission Forms are a tool you can use to request User Generated Content (UGC) and allow guests to submit photos, video, and/or messages. You can then curate, display, and unpublish the content as you see fit.",
          },
          {
            id: "widgets",
            icon: "view_module",
            text: "Widgets",
            to: "/widgets",
            tooltip: `Widgets are all of the types of content that you can share that exist outside of the Commons’ “stage.” Click the down karet to explore.`,
            children: [
              {
                id: "widgets__content_sections",
                text: "Content Sections",
                to: "/widgets/content-sections",
                tooltip:
                  "Content Sections are rows of thumbnails (content blocks) that can be used to house pre-recorded video content, downloads, popups w/ imagery & copy, or external links. These can exist on any page but are not a part of the timeline.",
              },
              ...(this.currentUser.active_project.available_addon_names.includes(
                "Addon::Ticketmaster"
              )
                ? [
                    {
                      id: "ticketmaster__venue_events_lists",
                      icon: "calendar",
                      text: "Venue Events Lists",
                      to: "/widgets/venue-events-lists",
                      tooltip: `Lists of Venue Events.`,
                    },
                  ]
                : []),
              {
                id: "widgets__text_blocks",
                text: "Text Blocks",
                to: "/widgets/text-blocks",
                tooltip:
                  "Text Blocks are big, graphic buttons. Each has a headline and description, but can also be paired with a unique background image. These can be linked to other pages on your Commons site or externally.",
              },
              {
                id: "widgets__people_lists",
                text: "People List",
                to: "/widgets/lists",
                tooltip:
                  "Lists allow you to create and edit running catalogs based on the data input. Think people lists, sponsor lists, etc.",
              },
              // {
              //   id: "widgets__submission_forms",
              //   text: "Submission Forms",
              //   to: "/widgets/submission-forms",
              //   tooltip:
              //     "Submission Forms are a tool you can use to request User Generated Content (UGC) and allow guests to submit photos, video, and/or messages. You can then curate, display, and unpublish the content as you see fit.",
              // },
              {
                id: "widgets__schedules",
                text: "Schedules",
                to: "/widgets/schedules",
                tooltip:
                  "Schedules are a high-level visualization of the events you want your attendees to know about. They can be tied to “events” or function as more general guides.",
              },
              {
                id: "widgets__countdowns",
                text: "Countdown Clocks",
                to: "/widgets/countdowns",
                tooltip:
                  "The Countdown Clock is a countdown clock :) Set a date & time and you can display the clock on any page on your site.",
              },

              {
                id: "widgets__notifications",
                text: "Notifications",
                to: "/widgets/notifications",
                tooltip:
                  "Notifications allow you to create and edit system generated alert popups that share information or drive users to another page via a call to action.",
              },

              {
                id: "widgets__html_blocks",
                text: "HTML Blocks",
                to: "/widgets/html-blocks",
                tooltip:
                  "HTML Blocks allow you to create custom HTML-based widgets for display on the site.",
              },
            ],
          },
          {
            id: "users",
            icon: "account",
            text: "Users",
            to: "/users/participants",
            tooltip:
              "Users are all your guests and team members for use on the live site. You can create custom “roles” for guests, which can then be used to create unique experiences within Commons. This is also where all contact info for you guests and team will live.",
          },
        ],

        {
          id: "chat_boxes",
          icon: "message",
          text: "Chat Boxes",
          to: "/chats",
          tooltip:
            "Chat Boxes allow you to create spaces for guests to come together and communicate with simple text messages. Each chat box is fully moderated by your team. Create as many as you’d like and place them on specific pages within the site.",
        },
        ...(this.currentUser.active_project.available_addon_names.includes(
          "Addon::DirectChat"
        )
          ? [
              {
                id: "direct_chat__boxes",
                icon: "direct_chat",
                text: "Direct Chat Boxes",
                to: "/direct-chat",
                tooltip: "Direct users messages",
              },
            ]
          : []),
        {
          id: "blog",
          icon: "blog",
          text: "Blog",
          to: "/blog",
          tooltip: "Here you can create Blog Articles",
        },
        {
          id: "content_management",
          icon: "contract_edit",
          text: "Content Management",
          to: "/content-management",
          tooltip: "Here you can manage content",
        },
        {
          id: "site_support",
          icon: "help_circle",
          text: "Site Support",
          to: "/support",
          tooltip:
            "Here you can create Site Support forms for guests to use if they have any questions or need to get in touch with a Commons admin.",
        },
        {
          id: "easy_updates",
          icon: "variables",
          text: "Easy Updates",
          to: "/variables",
          tooltip: "Easy Updates are used to replace text and images",
        },
        {
          id: "tools_and_authentication",
          icon: "addons",
          text: "Tools & Authentication",
          to: "/addons",
          tooltip:
            "Here you can enable site authentication using a CAS or social media platform for Page or Chat Box access. Also, you'll have access to other tools including Google Analytics.",
        },
        {
          id: "admin_logs",
          icon: "math_log",
          text: "Admin Logs",
          to: "/logs",
          tooltip:
            "With Admin Logs you can watch on admin's interactions in current project.",
        },
      ];

      if (
        this.currentUser.active_project.available_addon_names.includes(
          "Addon::RegistrationForms"
        )
      ) {
        const widgetsItem = items.find((item) => item.id === "widgets");
        if (widgetsItem && widgetsItem.children) {
          widgetsItem.children.push({
            id: "widgets__registration_forms",
            text: "Registration Forms",
            to: "/widgets/registration-forms",
            tooltip:
              "Registration Forms are custom forms for user sign ups, where you can add custom input fields beyond email and password.",
          });
        }
      }

      if (
        this.currentUser.active_project.available_addon_names.includes(
          "Addon::DeveloperTools"
        )
      ) {
        items.push({
          id: "dev_tools",
          icon: "dev_to",
          text: "Developer Tools",
          to: "/dev-tools",
          children: [
            {
              id: "dev_tools__custom_components",
              text: "Custom Components",
              to: "/dev-tools/custom-components",
            },
            {
              id: "dev_tools__presentations",
              text: "Presentations",
              to: "/dev-tools/presentations",
            },
            {
              id: "dev_tools__image_library",
              text: "Image Library",
              to: "/dev-tools/image-library",
              tooltip: `The Image Library is where you can upload images that can be referenced when writing code for custom components, CSS styles, and HTML blocks.`,
            },
          ],
        });
      }

      return items.filter((item) => {
        if (hiddenIds.includes(item.id)) return false;

        if (item.children) {
          item.children = item.children.filter(
            (child) => !hiddenIds.includes(child.id)
          );
        }

        return true;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.sidebar {
  border-right: 2px solid #dcdce0;

  &::v-deep .v-navigation-drawer__content {
    padding: 20px 17px 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &-subitem {
    padding-left: 88px;
  }

  &-item {
    padding-left: 45px;

    &::v-deep .v-list-item__title {
      color: var(--v-text-base) !important;
      font-weight: bold !important;
    }

    &::v-deep .v-list-item__icon {
      margin-right: 18px;
    }
  }

  &-item,
  &-subitem {
    &.active {
      background: var(--v-primary-base);

      &,
      &::v-deep .v-list-item__title {
        color: white !important;
      }

      &:before {
        opacity: 0;
      }
    }
  }
  &-group {
    &::v-deep .v-list-group__header {
      padding: 0 !important;
    }

    &__toggle-icon {
      position: relative;
      left: -8px;
    }
  }
}

.logo {
  display: none;
}

@media screen and (max-width: 1263px) {
  .logo {
    display: block;
    padding-left: 55px;
    margin-bottom: 20px;
  }

  .sidebar {
    &::v-deep .v-navigation-drawer__content {
      padding-top: 20px;
    }
  }
}
</style>
